// useJourneyMap.js
// Shared `/journeys?board=saras` slug -> journey row map, extracted from
// StemMissionLauncher.js:110-130 and SummerCampJourneyLauncher.js:135-150 so
// ExploreSection's mission/journey pills resolve journeys the same way the
// original launchers did. See docs/superpowers/specs/2026-09-16-home-redesign.md
// §5 and §11 "Frontend follow-ups folded into later packages" (WP7).
//
// `studentEmail` opts into the B4 per-student `progress` field on each
// journey row. The backend requires a Bearer token whenever `student_email`
// is sent (401 otherwise for the *whole* request), so `student_email` and
// the Authorization header are only ever sent together — passing
// `studentEmail` without `authToken` (or vice versa) silently falls back to
// the byte-identical no-param request instead of risking a 401.
import { useEffect, useState } from "react";
import axios from "axios";

import { FASTAPI_BASE_URL } from "../../../config";

const useJourneyMap = ({ studentEmail, authToken } = {}) => {
  const [journeysBySlug, setJourneysBySlug] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchJourneyCatalog = async () => {
      setLoading(true);
      try {
        const canRequestProgress = !!(studentEmail && authToken);
        const params = canRequestProgress
          ? { board: "saras", student_email: studentEmail }
          : { board: "saras" };
        const config = canRequestProgress
          ? { params, headers: { Authorization: `Bearer ${authToken}` } }
          : { params };

        const response = await axios.get(`${FASTAPI_BASE_URL}/journeys`, config);
        const rows = Array.isArray(response.data) ? response.data : [];
        const nextBySlug = {};
        rows.forEach((journey) => {
          const slug = String(journey?.slug || "").trim();
          if (slug) nextBySlug[slug] = journey;
        });
        if (!cancelled) setJourneysBySlug(nextBySlug);
      } catch (err) {
        console.error("Error fetching journey catalog:", err);
        if (!cancelled) setJourneysBySlug({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchJourneyCatalog();
    return () => {
      cancelled = true;
    };
  }, [studentEmail, authToken]);

  return { journeysBySlug, loading };
};

export default useJourneyMap;
